import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { usePermissions } from '@/hooks/usePermissions';
import { useTeamStore } from '@/stores/team-store';
import { usePlaylistSelectionStore } from '@/features/playlists/stores/playlist-selection-store';
import { useScheduleSelectionStore } from '@/features/schedules/stores/schedule-selection-store';
import { useSearchRecentsStore } from '../stores/search-recents-store';
import { ENTITY_ROUTES, ENTITY_PERMISSION_MAP, FOLDER_TYPE_ROUTES } from '../utils/entity-types';

interface UseSearchItemNavOptions {
  /**
   * Called BEFORE navigation (e.g. dropdown uses this to close its panel).
   * Not called on no-op paths (unknown entity type).
   */
  onBeforeNavigate?: () => void;
}

interface SearchItem extends Record<string, unknown> {
  _id: string;
}

interface FolderItem {
  _id: string;
  name: string;
  type?: string;
  path?: string;
  teamId?: string;
}

interface TeamItem {
  _id: string;
  name: string;
}

interface MemberItem {
  teamId: string;
  teamName: string;
}

/**
 * Single dispatcher for global-search row clicks. Handles all 10 entity types
 * with the exact same permission + folder-security + deep-link logic used by
 * the top-nav dropdown today (`GlobalSearchBar.handleSelect` etc.).
 *
 * Row click behavior stays identical whether the click happens inside the
 * dropdown or on the full search-results page.
 */
export function useSearchItemNav({ onBeforeNavigate }: UseSearchItemNavOptions = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const { isRootSuperAdmin, isTeamAdmin, canEdit } = usePermissions();

  /**
   * Content items — screens, assets, playlists, schedules, engages. Applies
   * 2-level permission check (role + folder security). Falls back to
   * folder-only navigation with a toast when the user lacks edit permission.
   */
  const handleItemSelect = useCallback(
    (entityType: string, id: string, item: SearchItem) => {
      onBeforeNavigate?.();
      const route = ENTITY_ROUTES[entityType];
      if (!route) return;

      const parentGroup = item.parentGroupInfo as
        | {
            name?: string;
            path?: string | null;
            teamId?: string;
            isFolderSecurityEdit?: boolean;
          }
        | undefined;

      // Level 1 (role) + Level 2 (folder security)
      const permEntity = ENTITY_PERMISSION_MAP[entityType];
      const hasRolePermission = !permEntity || canEdit(permEntity);
      let hasFolderPermission = true;
      if (parentGroup?.isFolderSecurityEdit === false) {
        const isSameTeam = !parentGroup.teamId || parentGroup.teamId === activeTeamId;
        hasFolderPermission = isRootSuperAdmin || (isTeamAdmin && isSameTeam);
      }

      // Pre-set selection stores so their URL sync effects don't race against
      // the deep-link and overwrite it.
      if (entityType === 'playlists') {
        usePlaylistSelectionStore.getState().setActivePlaylist(id);
      } else if (entityType === 'schedules') {
        useScheduleSelectionStore.getState().selectSchedule(id);
      }

      // Build URL: always include folder path, add id only when user has permission
      const params = new URLSearchParams();
      if (parentGroup?.name) {
        const parentPath = parentGroup.path && parentGroup.path !== '/' ? parentGroup.path : '';
        params.set('path', `${parentPath}/${parentGroup.name}`);
        if (parentGroup.teamId && parentGroup.teamId !== activeTeamId) {
          params.set('teamId', parentGroup.teamId);
        }
      }

      if (!hasRolePermission || !hasFolderPermission) {
        toast.error(t('You do not have permission to edit this asset'));
      } else {
        params.set('id', id);
        // Canvas (Designer) assets — open in the Designer editor, not the
        // generic asset detail dialog. Only reached when perms passed (the
        // no-perm path still falls through to the folder view + toast).
        // Mirrors `useOpenAssetEditor`'s canvas branch precisely, using
        // fields the search response already selects on assets (`_id`,
        // `path`, `teamId`, `engage`, `appType`).
        if ((entityType === 'assets' || entityType === 'engages') && item.appType === 'canvas') {
          const designerParams = new URLSearchParams();
          designerParams.set('asset_id', id);
          if (typeof item.path === 'string' && item.path) {
            designerParams.set('path', item.path);
          }
          if (typeof item.teamId === 'string' && item.teamId) {
            designerParams.set('teamId', item.teamId);
          }
          if (item.engage) designerParams.set('engage', '1');
          const currentRoute = window.location.pathname + window.location.search;
          if (currentRoute && !currentRoute.startsWith('/app/assetManagement')) {
            designerParams.set('returnTo', currentRoute);
          }
          navigate(`/app/designManagement?${designerParams.toString()}`);
          return;
        }
      }

      const qs = params.toString();
      navigate(qs ? `${route}?${qs}` : route);
    },
    [onBeforeNavigate, navigate, t, activeTeamId, isRootSuperAdmin, isTeamAdmin, canEdit],
  );

  const handleFolderSelect = useCallback(
    (folder: FolderItem) => {
      onBeforeNavigate?.();
      const folderTypeLower = folder.type?.toLowerCase();
      const route =
        (folderTypeLower &&
          (FOLDER_TYPE_ROUTES as Record<string, string | undefined>)[folderTypeLower]) ??
        ENTITY_ROUTES.assets;
      // Root-level folders have path=null or path='/'; nested have path='/Parent'
      const parentPath = folder.path && folder.path !== '/' ? folder.path : '';
      const folderPath = `${parentPath}/${folder.name}`;
      const params = new URLSearchParams({ path: folderPath });
      // Cross-team share — folder belongs to another team's tree, accessed via
      // folder-level share to me.
      if (folder.teamId && folder.teamId !== activeTeamId) {
        params.set('teamId', folder.teamId);
        // Mark as a "Shared with me" arrival so the destination page's
        // breadcrumb trims inaccessible ancestors (`FolderBreadcrumb`
        // reads `fromShared` + `sharedRoot`) — mirrors what the "Shared
        // with me" sidebar entry sets when the user clicks a folder there.
        // The search dropdown's own subtitle already renders "Shared with
        // me" for these; without this pair the destination page would
        // still leak the parent name in the header.
        params.set('shared', '1');
        params.set('sharedRoot', folderPath);
      }
      navigate(`${route}?${params.toString()}`);
    },
    [onBeforeNavigate, navigate, activeTeamId],
  );

  const handleFeedSelect = useCallback(
    (id: string) => {
      onBeforeNavigate?.();
      navigate(`${ENTITY_ROUTES.assets}?feed_id=${id}`);
    },
    [onBeforeNavigate, navigate],
  );

  /**
   * Deep-link into Account Members filtered to a specific team. Both team-row
   * and member-row clicks scope the destination page to that one team so the
   * "one row per (member, team) pairing" split carries through visually — row
   * 1 (Default) and row 2 (Team A) each land on their own team section.
   */
  const handleTeamSelect = useCallback(
    (team: TeamItem) => {
      onBeforeNavigate?.();
      const params = new URLSearchParams({ teamId: team._id, teamName: team.name });
      navigate(`/app/s/account-members?${params.toString()}`);
    },
    [onBeforeNavigate, navigate],
  );

  const handleMemberSelect = useCallback(
    (member: MemberItem) => {
      onBeforeNavigate?.();
      const params = new URLSearchParams({ teamId: member.teamId, teamName: member.teamName });
      navigate(`/app/s/account-members?${params.toString()}`);
    },
    [onBeforeNavigate, navigate],
  );

  /**
   * Jump into the folder a result LIVES IN — the Location column's click
   * target on the search-results table (`LocationCell`).
   *
   * Different from `handleFolderSelect`, which opens a folder that IS the
   * result. Here the folder is context: the route comes from the RESULT's
   * entity type (a screen's location opens the Screens page at that path),
   * except when the result is itself a folder, where its own `type` picks the
   * feature page — same rule `handleFolderSelect` uses.
   */
  const navigateToContainerFolder = useCallback(
    (entityType: string, item: Record<string, unknown>, path: string, teamId?: string) => {
      onBeforeNavigate?.();
      const route =
        entityType === 'folders'
          ? ((item.type as string | undefined) &&
              (FOLDER_TYPE_ROUTES as Record<string, string | undefined>)[
                (item.type as string).toLowerCase()
              ]) ||
            ENTITY_ROUTES.assets
          : ENTITY_ROUTES[entityType];
      if (!route) return;
      const params = new URLSearchParams();
      if (path) params.set('path', path);
      if (teamId && teamId !== activeTeamId) params.set('teamId', teamId);
      const qs = params.toString();
      navigate(qs ? `${route}?${qs}` : route);
    },
    [onBeforeNavigate, navigate, activeTeamId],
  );

  /**
   * Unified dispatcher — picks the right handler by `entityType`. Consumers
   * that don't need per-type control can just call this.
   */
  const selectItem = useCallback(
    (entityType: string, item: Record<string, unknown>) => {
      // Feed the dropdown's "Recent" section. Recorded here rather than at each
      // call site so both surfaces (dropdown + search page) contribute, and so
      // a future consumer of `selectItem` gets it for free.
      useSearchRecentsStore.getState().recordEntity(entityType, item);

      if (entityType === 'folders') {
        handleFolderSelect(item as unknown as FolderItem);
      } else if (entityType === 'feeds') {
        handleFeedSelect(item._id as string);
      } else if (entityType === 'operationalSchedules') {
        onBeforeNavigate?.();
        const id = item._id as string;
        navigate(
          id
            ? `${ENTITY_ROUTES.operationalSchedules}?id=${id}`
            : ENTITY_ROUTES.operationalSchedules,
        );
      } else if (entityType === 'teams') {
        handleTeamSelect(item as unknown as TeamItem);
      } else if (entityType === 'members') {
        handleMemberSelect(item as unknown as MemberItem);
      } else {
        handleItemSelect(entityType, item._id as string, item as SearchItem);
      }
    },
    [
      onBeforeNavigate,
      navigate,
      handleItemSelect,
      handleFolderSelect,
      handleFeedSelect,
      handleTeamSelect,
      handleMemberSelect,
    ],
  );

  return {
    selectItem,
    navigateToContainerFolder,
    handleItemSelect,
    handleFolderSelect,
    handleFeedSelect,
    handleTeamSelect,
    handleMemberSelect,
  };
}
